import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';

const TenantContext = createContext();

export const TenantProvider = ({ children }) => {
  const { user, isOperator, isGlobalAdmin } = useAuth();
  const [activeTenant, setActiveTenant] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  useEffect(() => {
    const loadTenants = async () => {
      if (!user?.roles) return;
      if (isGlobalAdmin()) {
        const res = await api.get('/tenants');
        const tenantList = res.data.map((tenant) => ({
          id: tenant.id,
          name: tenant.name,
          role: 'GlobalAdmin',
        }));
        setTenants(tenantList);
        const savedTenant = localStorage.getItem('activeTenant');
        if (savedTenant && tenantList.find((t) => t.id === savedTenant)) {
          setActiveTenant(savedTenant);
        } else if (tenantList.length > 0) {
          setActiveTenant(tenantList[0].id);
        }
        return;
      }

      const userTenants = user.roles.map((r) => ({ id: r.tenant_id, role: r.role }));
      setTenants(userTenants);

      if (isOperator()) {
        const savedTenant = localStorage.getItem('activeTenant');
        if (savedTenant && userTenants.find((t) => t.id === savedTenant)) {
          setActiveTenant(savedTenant);
        } else if (userTenants.length > 0) {
          setActiveTenant(userTenants[0].id);
        }
      } else if (userTenants.length > 0) {
        setActiveTenant(userTenants[0].id);
      }
    };

    loadTenants().catch((error) => {
      console.error('Error loading tenants:', error);
      setTenants([]);
      setActiveTenant(null);
    });
  }, [user, isOperator, isGlobalAdmin]);

  const loadOnboardingStatus = async (tenantId) => {
    if (!tenantId) return;
    try {
      setOnboardingLoading(true);
      const res = await api.get(`/onboarding/status?tenant_id=${tenantId}`);
      const status = res.data;
      if (status?.onboarding_status === 'APPROVED' && !status?.is_ready_to_operate) {
        status.is_ready_to_operate = true;
      }
      setOnboardingStatus(status);
    } catch (error) {
      console.error('Error loading onboarding status:', error);
      setOnboardingStatus(null);
    } finally {
      setOnboardingLoading(false);
    }
  };

  useEffect(() => {
    if (activeTenant) {
      loadOnboardingStatus(activeTenant);
    }
  }, [activeTenant]);

  useEffect(() => {
    if (!activeTenant) return;
    const interval = setInterval(() => {
      loadOnboardingStatus(activeTenant);
    }, 60000);
    return () => clearInterval(interval);
  }, [activeTenant]);

  const switchTenant = (tenantId) => {
    setActiveTenant(tenantId);
    localStorage.setItem('activeTenant', tenantId);
  };

  return (
    <TenantContext.Provider
      value={{
        activeTenant,
        setActiveTenant: switchTenant,
        tenants,
        onboardingStatus,
        onboardingLoading,
        refreshOnboardingStatus: () => loadOnboardingStatus(activeTenant),
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => useContext(TenantContext);
